import React, { createContext, useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

//hooks
import useAuth from "../hooks/useAuth";

//interfaces
import { Branding } from "../interfaces/Branding";
import { listLayout } from "../interfaces/Lists";
import { Profile } from "../interfaces/User";

//services
import { getOrgShortName } from "../services/authservice";
import { getDeeplinkUrl } from "../services/DeeplinkService";
import { CAS } from 'thrive-chat';
import useWindowSize from "../hooks/useWindowSize";
import { useObservable } from "thrive-observable-react";

export const brandingDefault: Branding = {
  bgColor: "",
  bgLogo: "",
  buttonColor: "",
  favIcon: "",
  logo: "",
  orgName: "",
};

export const defaultProfile = {
  userEditableFields: [],
  email: "",
  firstName: "",
  lastName: "",
  userName: "",
  displayName: "",
  jobRole: "",
  profilePictureUrl: "/img/general/no_profile_picture@2x.png",
  id: 0,
  profileEnabled: false,
  displayNameSet: false,
  profileEmail: "",
  profilePhoneNumber: "",
  department: "",
  profilePicture: "",
  isActive: false,
};

export interface listSettings {
  displayDateType: string;
  displayGradients: boolean;
  displayHero: boolean;
  displayIcons: boolean;
  layout: listLayout;
}

export const listSettingsDefault: listSettings = {
  displayDateType: "",
  displayGradients: true,
  displayHero: false,
  displayIcons: false,
  layout: listLayout.smallList,
};

export interface IAppContext {
  loading: boolean;
  setLoading: (value: boolean) => void;
  appBranding: Branding | null;
  setAppBranding: (value: Branding | null) => void;
  layout: listLayout;
  setLayout: (layout: listLayout) => void;
  pageName: string;
  setPageName: (name: string) => void;
  listSettings: listSettings | null;
  setListSettings: (listSettings: listSettings) => void;
  userProfile: Profile;
  setUserProfile: (userProfile: Profile) => void;
  handleAttemptedNavigation: (returnPath?: string) => void;
  showBackToChatButton: boolean,
  backToChatClick: () => void,
}

export const appContextDefaults = {
  loading: false,
  setLoading: () => {
    return;
  },
  appBranding: brandingDefault,
  setAppBranding: () => {
    return;
  },
  layout: listLayout.smallList,
  setLayout: () => {
    return;
  },
  pageName: "",
  setPageName: () => {
    return;
  },
  listSettings: listSettingsDefault,
  setListSettings: () => {
    return;
  },
  userProfile: defaultProfile,
  setUserProfile: () => {
    return;
  },
  handleAttemptedNavigation: () => {
    return;
  },
  showBackToChatButton: false,
  backToChatClick: () => {return}
};

export const AppContext = createContext<IAppContext>(appContextDefaults);

export default function AppProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [loading, setLoading] = useState<boolean>(false);
  const [appBranding, setAppBranding] = useState<Branding | null>(null);
  const [layout, setLayout] = useState<listLayout>(listLayout.smallList);
  const [pageName, setPageName] = useState<string>("");
  const [listSettings, setListSettings] = useState<listSettings | null>(null);
  const [userProfile, setUserProfile] = useState<Profile>(defaultProfile);
  const { setAuthFromLocalStorage } = useAuth();
  const navigate = useNavigate();
  const [showBackToChatButton, setShowBackToChatButton] = useState<boolean>(false);
  const {windowSize, checkSize} = useWindowSize();
  const isInPeopleSearch = useObservable(CAS.isInPeopleSearch);
  const isInConversation = useObservable(CAS.isInActiveConversation);

  //navigate to a valid attempted URL within local storage,
  //or default navigation to the applibrary
  const handleAttemptedNavigation = (returnPath?: string) => {
    
    if(returnPath) {
      navigate(`/${decodeURIComponent(returnPath)}`, { replace: true });
    }
    else {
      navigate(getDeeplinkUrl() || `/${getOrgShortName()}/applibrary`, { replace: true });
    }
  };

  useEffect(() => {
    setAuthFromLocalStorage();
  }, []);

  useEffect(()=> {
      updateSideBarView();
  }, [isInPeopleSearch, isInConversation]);

  // Update when window size changes
  useEffect(() => {
    updateBackToChatButtonVisibility();
  }, [windowSize]);

  const updateSideBarView = () => {
      checkSize();
      updateBackToChatButtonVisibility();
  }
  
  const updateBackToChatButtonVisibility = () => { 
    const isVisable = (CAS.isInActiveConversation.value || CAS.isInPeopleSearch.value) && windowSize?.width <= 616;
    setShowBackToChatButton(isVisable);
  };
  
  const backToChatClick = () => {
    CAS.goBackToMenu();
  }

  const contextValues: IAppContext = useMemo(() => {
    return {
      loading,
      setLoading,
      appBranding,
      setAppBranding,
      layout,
      setLayout,
      pageName,
      setPageName,
      listSettings,
      setListSettings,
      userProfile,
      setUserProfile,
      handleAttemptedNavigation,
      showBackToChatButton,
      backToChatClick
    };
  }, [loading, appBranding, layout, pageName, listSettings, userProfile, showBackToChatButton]);

  return (
    <AppContext.Provider value={contextValues}>{children}</AppContext.Provider>
  );
}
